import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Element } from "react-scroll";
import { Calendar, MapPin, ExternalLink, Building } from "lucide-react";
import { useTranslation } from "react-i18next";
// @ts-ignore
import cv from "../assets/cv.pdf";

interface WorkExperience {
  company: string;
  position: string;
  location: string;
  duration: string;
  description: string[];
  technologies: string[];
  achievements: string[];
}

const Work: React.FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const experiences: WorkExperience[] = [
    {
      company: t("work.experience.demandLocal.company"),
      position: t("work.experience.demandLocal.position"),
      location: t("work.experience.demandLocal.location"),
      duration: t("work.experience.demandLocal.duration"),
      description: t("work.experience.demandLocal.responsibilities", {
        returnObjects: true,
      }) as string[],
      technologies: [
        "React",
        "Node.js",
        "PHP",
        "TypeScript",
        "AWS",
        "MongoDB",
        "ClickHouse",
        "Snowflake",
      ],
      achievements: t("work.experience.demandLocal.achievements", {
        returnObjects: true,
      }) as string[],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <Element name="work">
      <section ref={ref} className="py-20 lg:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-transparent to-slate-900/50" />

        <div className="relative z-10 section-padding container-width">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span className="gradient-text">{t("work.title")}</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                {t("work.subtitle")}
              </p>
            </motion.div>

            <div className="space-y-8">
              {experiences.map((experience, index) => (
                <motion.div
                  key={experience.company}
                  variants={itemVariants}
                  custom={index}
                  className="glass-card p-8 lg:p-12 rounded-2xl hover:bg-white/20 transition-all duration-300 group"
                  whileHover={{
                    scale: 1.02,
                    transition: { type: "spring", stiffness: 300, damping: 20 },
                  }}
                >
                  <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1">
                      <div className="flex items-start space-x-4 mb-6">
                        <motion.div
                          className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6 }}
                        >
                          <Building className="w-6 h-6 text-white" />
                        </motion.div>
                        <div>
                          <h3 className="text-xl font-bold text-white mb-1 group-hover:text-blue-300 transition-colors">
                            {experience.company}
                          </h3>
                          <p className="text-lg font-semibold text-blue-400 mb-2">
                            {experience.position}
                          </p>
                          <div className="flex items-center text-gray-400 text-sm mb-2">
                            <Calendar className="w-4 h-4 mr-2" />
                            {experience.duration}
                          </div>
                          <div className="flex items-center text-gray-400 text-sm">
                            <MapPin className="w-4 h-4 mr-2" />
                            {experience.location}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-2">
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-white mb-4">
                          {t("work.keyResponsibilities")}
                        </h4>
                        <ul className="space-y-2">
                          {experience.description.map((desc, descIndex) => (
                            <li
                              key={descIndex}
                              className="text-gray-300 flex items-start"
                            >
                              <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                              {desc}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-white mb-4">
                          {t("work.keyAchievements")}
                        </h4>
                        <ul className="space-y-2">
                          {experience.achievements.map(
                            (achievement, achIndex) => (
                              <li
                                key={achIndex}
                                className="text-gray-300 flex items-start"
                              >
                                <span className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0" />
                                {achievement}
                              </li>
                            )
                          )}
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-white mb-4">
                          {t("work.technologiesUsed")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {experience.technologies.map((tech) => (
                            <motion.span
                              key={tech}
                              className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium border border-blue-500/30"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              {tech}
                            </motion.span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div variants={itemVariants} className="text-center mt-16">
              <motion.a
                href={cv}
                download="James-Bell-CV.pdf"
                className="btn-primary inline-flex items-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ExternalLink size={18} />
                <span>{t("work.viewFullResume")}</span>
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Element>
  );
};

export default Work;
